// Shared marketing footer — link columns + brand block + privacy badge +
// copyright + runtime language switch (mockup market/footer.jsx). Visual
// layer only; the footer links point at the route stubs (D2-D5 fill the
// pages). The language toggle flips the i18n locale live via
// changeLanguage; main.tsx's languageChanged hook re-syncs <html lang>.

import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { storeLocale, type LocaleCode } from "@novemai/i18n-sdk";

import { Wordmark } from "@/components/Wordmark";
import { localeStorage } from "@/i18n/locale-storage";
import { DEMO_REQUEST_PATH } from "@/lib/site-links";
import { cn } from "@/lib/utils";

interface FooterLink {
  readonly key: string;
  readonly labelKey: string;
  readonly to: string;
}

interface FooterColumn {
  readonly key: string;
  readonly titleKey: string;
  readonly links: readonly FooterLink[];
}

// Footer columns link ONLY to surfaces that exist. The product items resolve to
// their per-module detail pages (/modules/<key>); company keeps the real pages
// (about/modules/pricing) + the demo CTA; legal carries the four selectable
// documents plus the existing privacy-security section. The former careers/
// customers/blog and docs/api/status links pointed at non-existent surfaces and
// were removed rather than dead-ended at a placeholder (FRONTEND_PORT_MAP §10;
// same call as the nav mega-menu). Social icons were removed for the same
// reason: no account URLs exist. Re-add as those surfaces ship.
const COLUMNS: readonly FooterColumn[] = [
  {
    key: "product",
    titleKey: "footer.columns.product.title",
    links: [
      { key: "ai_assistant", labelKey: "footer.columns.product.items.ai_assistant", to: "/ai" },
      { key: "requests", labelKey: "footer.columns.product.items.requests", to: "/modules/requests" },
      { key: "leave", labelKey: "footer.columns.product.items.leave", to: "/modules/leave" },
      { key: "payroll", labelKey: "footer.columns.product.items.payroll", to: "/modules/payroll" },
      { key: "attendance", labelKey: "footer.columns.product.items.attendance", to: "/modules/attendance" },
      { key: "reports", labelKey: "footer.columns.product.items.reports", to: "/modules/reports" },
    ],
  },
  {
    key: "company",
    titleKey: "footer.columns.company.title",
    links: [
      { key: "about", labelKey: "footer.columns.company.items.about", to: "/company" },
      { key: "modules", labelKey: "footer.columns.company.items.modules", to: "/modules" },
      { key: "pricing", labelKey: "footer.columns.company.items.pricing", to: "/pricing" },
      { key: "demo", labelKey: "footer.columns.company.items.demo", to: DEMO_REQUEST_PATH },
      { key: "help", labelKey: "footer.columns.company.items.help", to: "/help" },
      { key: "contact", labelKey: "footer.columns.company.items.contact", to: "/contact" },
    ],
  },
  {
    key: "legal",
    titleKey: "footer.columns.legal.title",
    links: [
      { key: "kvkk_notice", labelKey: "footer.columns.legal.items.kvkk_notice", to: "/legal?doc=kvkk" },
      { key: "privacy", labelKey: "footer.columns.legal.items.privacy", to: "/legal?doc=privacy" },
      { key: "terms", labelKey: "footer.columns.legal.items.terms", to: "/legal?doc=terms" },
      { key: "cookies", labelKey: "footer.columns.legal.items.cookies", to: "/legal?doc=cookies" },
      { key: "security", labelKey: "footer.columns.legal.items.security", to: "/legal?doc=privacy#privacy-security" },
    ],
  },
];

export function MarketingFooter(): React.ReactElement {
  const { t, i18n } = useTranslation();

  const setLang = (lng: LocaleCode): void => {
    storeLocale(lng, localeStorage);
    void i18n.changeLanguage(lng);
  };

  const langBtnClass = (active: boolean): string =>
    cn(
      "font-sans text-[13px] no-underline focus:outline-none focus-visible:rounded-[4px] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
      active
        ? "font-semibold text-[var(--accent-hover)]"
        : "text-[var(--fg-muted)] hover:text-[var(--fg-secondary)]",
    );

  return (
    <footer className="border-t border-[var(--border-subtle)] bg-[var(--bg-raised)] px-6 pb-8 pt-14 lg:px-8">
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-10 grid grid-cols-2 gap-10 md:grid-cols-3 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Wordmark size={19} className="mb-3.5" />
            <p className="mb-5 max-w-[240px] font-serif text-sm leading-relaxed text-[var(--fg-muted)]">
              {t("footer.tagline")}
            </p>
            <div className="flex flex-wrap gap-2">
              {/* The only badge that is a shipped control rather than a promise.
                  ISO 27001 / SOC 2 badges and the "expected Q3 2026" note were
                  removed: neither certification is held, and a dated promise to a
                  regulated buyer becomes a broken one the day it slips. */}
              <span className="rounded-[6px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-2.5 py-1 font-sans text-[11px] font-semibold text-[var(--fg-muted)]">
                {t("footer.certs.kvkk")}
              </span>
            </div>
          </div>

          {/* Nav columns */}
          {COLUMNS.map((col) => (
            <nav key={col.key} aria-label={t(col.titleKey)}>
              <p className="mb-3.5 font-sans text-xs font-semibold uppercase tracking-[0.07em] text-[var(--fg-muted)]">
                {t(col.titleKey)}
              </p>
              <ul className="flex flex-col gap-2.5">
                {col.links.map((link) => (
                  <li key={link.key}>
                    <Link
                      to={link.to}
                      className="font-sans text-[13.5px] text-[var(--fg-secondary)] no-underline hover:text-[var(--accent-hover)] focus:outline-none focus-visible:rounded-[4px] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                    >
                      {t(link.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-subtle)] pt-5">
          <span className="font-sans text-[12.5px] text-[var(--fg-muted)]">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </span>
          <div className="flex items-center gap-5">
            <span className="font-serif text-[12.5px] italic text-[var(--fg-muted)]">
              {t("footer.location")}
            </span>
            <div className="flex items-center gap-3" role="group" aria-label={t("footer.language_label")}>
              <button type="button" onClick={(): void => setLang("tr-TR")} className={langBtnClass(i18n.language === "tr-TR")}>
                {t("footer.lang_tr")}
              </button>
              <button type="button" onClick={(): void => setLang("en-US")} className={langBtnClass(i18n.language === "en-US")}>
                {t("footer.lang_en")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
